import React, { useState } from 'react';
import { Info, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import {
  balanceNarrative,
  balanceParts,
  balanceProjectionLabel,
  balanceNumber,
} from '@/lib/ferieBalanceExplanation';

const formatDays = (value) => balanceNumber(value).toLocaleString('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const signedDays = (value) => {
  const number = balanceNumber(value);
  return `${number < 0 || Object.is(number, -0) ? '−' : '+'}${formatDays(Math.abs(number))}`;
};

const quoteSourceLabel = (source) => ({
  LEGACY_STORICO: 'Storico legacy',
  BASELINE: 'Baseline',
  MATURAZIONE: 'Maturazione',
  RUNTIME_SALDO: 'Saldo corrente',
}[source] || source || 'Quota');

function QuoteBreakdown({ balance, testid, canEdit = false, onSaved }) {
  const [editingQuote, setEditingQuote] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const quotes = Array.isArray(balance?.quote)
    ? balance.quote
    : (Array.isArray(balance?.riportoDettaglio?.quote) ? balance.riportoDettaglio.quote : []);
  if (!quotes.length) return null;
  const expiring = quotes
    .filter((quote) => quote.scadenza && quote.stato !== 'SCADUTA')
    .reduce((total, quote) => total + Number(quote.quotaDisponibile || 0), 0);
  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid={`${testid}-quotes`}>
      <div className="text-xs font-bold uppercase tracking-widest text-amber-900">Quote riportate</div>
      {expiring > 0 && (
        <div className="mt-1 text-xs text-amber-800">
          {formatDays(expiring)}g hanno una scadenza configurata: consumate prima le quote con scadenza più vicina.
        </div>
      )}
      <div className="mt-2 space-y-2">
        {quotes.map((quote) => {
          const expired = quote.stato === 'SCADUTA' || Number(quote.quotaDisponibile || 0) === 0;
          const adjustments = quote.rettificheDettaglio || [];
          const isEditing = editingQuote === quote.id;
          return (
            <div key={quote.id || `${quote.annoOrigine}-${quote.fonte}`} className="rounded-lg border border-amber-100 bg-white px-3 py-2 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-stone-800">
                  Anno origine {quote.annoOrigine} · {quoteSourceLabel(quote.fonte)}
                </div>
                <div className="text-stone-500">
                  {quote.scadenza ? `Scadenza ${quote.scadenza}` : 'Senza scadenza configurata'}
                  {!quote.storicoRicostruibile && ' · dettaglio storico non ricostruibile'}
                </div>
              </div>
              <div className={`font-semibold ${expired ? 'text-red-700' : 'text-amber-900'}`}>
                {formatDays(quote.quotaDisponibile ?? quote.quantitaResidua)}g disponibili
              </div>
              </div>
              {adjustments.length > 0 && (
                <div className="mt-2 border-t border-amber-100 pt-2 text-[11px] text-stone-500">
                  {adjustments.map((adjustment) => (
                    <div key={adjustment.id} className="flex flex-wrap justify-between gap-2">
                      <span>{adjustment.motivo}</span>
                      <span className="font-semibold text-stone-700">{signedDays(adjustment.quantita)}g</span>
                    </div>
                  ))}
                </div>
              )}
              {canEdit && quote.id && quote.stato !== 'SCADUTA' && (
                <div className="mt-2 border-t border-amber-100 pt-2">
                  {!isEditing ? (
                    <button type="button" className="font-semibold text-brand-blue hover:underline" onClick={() => {
                      setEditingQuote(quote.id);
                      setQuantity('');
                      setReason('');
                    }}>Aggiungi rettifica quota</button>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-[8rem_1fr_auto] sm:items-end">
                      <label className="font-semibold text-stone-600">Giorni (+ / −)
                        <input type="number" step="0.01" value={quantity} onChange={(event) => setQuantity(event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-amber-200 px-2 font-normal" />
                      </label>
                      <label className="font-semibold text-stone-600">Motivo obbligatorio
                        <input value={reason} onChange={(event) => setReason(event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-amber-200 px-2 font-normal" />
                      </label>
                      <div className="flex gap-1">
                        <Button type="button" size="sm" disabled={busy} className="h-9 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={async () => {
                          const amount = Number(quantity);
                          if (!amount || !reason.trim()) {
                            toast.error('Inserisci quantità e motivo');
                            return;
                          }
                          setBusy(true);
                          try {
                            await apiFetch(`/api/ferie/quote/${quote.id}/rettifiche`, {
                              method: 'POST',
                              body: JSON.stringify({ quantita: amount, motivo: reason.trim() }),
                            });
                            toast.success('Rettifica quota registrata');
                            setEditingQuote(null);
                            onSaved?.();
                          } catch (error) {
                            toast.error(error.message || 'Rettifica non salvata');
                          } finally {
                            setBusy(false);
                          }
                        }}><Save className="mr-1 h-3.5 w-3.5" />Salva</Button>
                        <Button type="button" size="sm" variant="ghost" className="h-9 rounded-full" onClick={() => setEditingQuote(null)}>Annulla</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoHint({ label, description }) {
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        aria-label={`Informazioni su ${label}`}
        title={description}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

function ExplanationRow({ label, value, sign, negative = false, description }) {
  const displayValue = sign ? signedDays(negative ? -Math.abs(value) : value) : formatDays(value);
  return (
    <div className="flex items-center justify-between gap-3 border-b border-stone-100 px-4 py-3 last:border-0">
      <span className="min-w-0 text-stone-700">
        {label}
        <InfoHint label={label} description={description} />
      </span>
      <span className="shrink-0 font-semibold text-stone-900">{displayValue} giorni</span>
    </div>
  );
}

export default function BalanceExplanation({
  balance,
  baseline,
  year,
  projection = false,
  policy = null,
  testid = 'ferie-balance-explanation',
  compact = false,
  canEdit = false,
  onSaved,
}) {
  const parts = balanceParts(balance, { baseline, projection });
  const projectionLabel = balanceProjectionLabel(year, projection);
  const policyLabel = {
    employee: 'Policy individuale',
    tenant: 'Policy aziendale/tenant',
    legacy: 'Configurazione precedente',
  }[policy?.source];

  return (
    <section
      className={`space-y-3 ${compact ? '' : 'rounded-2xl border border-stone-200 bg-white p-5'}`}
      data-testid={testid}
    >
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 text-center shadow-sm">
        <div className="font-display text-3xl font-semibold text-brand-green">{formatDays(parts.available)}</div>
        <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-stone-600">Giorni disponibili</div>
        <div className="mt-1 text-xs text-stone-600">{projectionLabel}</div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
        <div>
          <h2 className="font-display font-semibold tracking-tight text-stone-900">Come si calcola il saldo</h2>
          <p className="mt-1 text-xs text-stone-500">
            La spiegazione usa il saldo restituito dal server per l&apos;anno selezionato.
          </p>
          {policyLabel && (
            <div className="mt-2 inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-800">
              {policyLabel}
              {policy?.effectiveFrom && ` · dal ${policy.effectiveFrom}`}
            </div>
          )}
        </div>
        <QuoteBreakdown balance={balance} testid={testid} canEdit={canEdit} onSaved={onSaved} />
        <div className="mt-3 overflow-hidden rounded-xl border border-stone-200 bg-white text-sm">
          <ExplanationRow
            label={parts.openingLabel}
            value={parts.opening}
            description={parts.hasBaseline
              ? 'Saldo consolidato alla data di riferimento. La baseline sostituisce il riporto nel calcolo.'
              : 'Saldo riportato dall’anno precedente e applicato all’anno selezionato.'}
          />
          <ExplanationRow
            label={parts.accruedLabel}
            value={parts.accrued}
            sign
            description={parts.accruedLabel === 'Maturato'
              ? 'Quota maturata e contabilizzata alla data di riferimento.'
              : 'Quota che il server prevede di maturare fino alla data di proiezione.'}
          />
          <ExplanationRow
            label="Goduto"
            value={parts.used}
            sign
            negative
            description="Ferie già utilizzate e consuntivate."
          />
          <ExplanationRow
            label="Programmato"
            value={parts.planned}
            sign
            negative
            description="Ferie già programmate ma non ancora godute."
          />
          <ExplanationRow
            label="Rettifiche"
            value={parts.adjustments}
            sign
            description="Correzioni amministrative applicate dal server."
          />
          <div className="flex flex-wrap items-center justify-between gap-2 bg-emerald-50 px-4 py-3 font-semibold text-brand-green">
            <span>{projectionLabel}</span>
            <span data-testid={`${testid}-result`}>{formatDays(parts.available)} giorni</span>
          </div>
        </div>

        <div className="mt-3 min-w-0 text-xs text-stone-600" data-testid={`${testid}-inline`}>
          <span className="break-words font-mono sm:whitespace-nowrap">
            {formatDays(parts.opening)} {signedDays(parts.accrued)} {signedDays(-parts.used)} {signedDays(-parts.planned)} {signedDays(parts.adjustments)} = {formatDays(parts.available)}
          </span>
        </div>
        {!parts.formulaMatches && (
          <p className="mt-2 text-xs text-amber-700">
            Il totale visualizzato segue il saldo restituito dal server.
          </p>
        )}
        <p className="mt-3 text-sm leading-6 text-stone-600" data-testid={`${testid}-narrative`}>
          {balanceNarrative(parts, year, projection)}
        </p>
      </div>
    </section>
  );
}